package co.uk.paxes.pinpoint.web

import kotlinx.browser.window
import kotlinx.browser.document
import org.w3c.dom.Audio
import org.w3c.dom.events.Event
import org.w3c.xhr.XMLHttpRequest

/**
 * Sound manager using Web Audio API for mobile compatibility.
 * Uses Web Audio API buffers for reliable playback on iOS/Android.
 */
object SoundManager {
    private var isMuted = false
    private var masterVolume = 0.4f
    private var initialized = false
    
    // Web Audio API context
    private var audioContext: dynamic = null
    
    // Audio buffers for Web Audio API
    private val audioBuffers = mutableMapOf<String, dynamic>()
    
    // Track if audio is unlocked
    private var audioUnlocked = false
    
    // Track if we're on mobile (use Web Audio API)
    private var isMobile = false
    
    // Keep-alive state
    private var keepAliveSource: dynamic = null
    private var keepAliveBuffer: dynamic = null
    private var heartbeatIntervalId: Int? = null
    private var hybridAudio: dynamic = null
    
    // Sound file mapping - extension added dynamically based on browser
    private val soundKeys = listOf(
        "pin_placed",
        "pin_selected",
        "score_basic",
        "score_basic_2",
        "score_basic_3",
        "score_good",
        "score_great",
        "score_epic",
        "pins_reversed",
        "joker_created",
        "error"
    )
    
    // Detect if we need MP3 (Safari/iOS) or can use OGG
    private var useMP3 = false

    fun initialize() {
        if (initialized) return
        initialized = true
        
        // Detect mobile/touch devices
        val userAgent = window.navigator.userAgent.lowercase()
        val hasTouch = js("'ontouchstart' in window || navigator.maxTouchPoints > 0") as Boolean
        isMobile = hasTouch || 
                   userAgent.contains("mobile") || 
                   userAgent.contains("android") ||
                   userAgent.contains("iphone") ||
                   userAgent.contains("ipad")
        
        // Detect Safari/iOS - they need MP3/MPG (don't support OGG)
        val isSafari = userAgent.contains("safari") && !userAgent.contains("chrome") && !userAgent.contains("chromium")
        val isIOS = userAgent.contains("iphone") || userAgent.contains("ipad") || userAgent.contains("ipod") ||
                    (userAgent.contains("macintosh") && hasTouch)
        useMP3 = isSafari || isIOS
        
        console.log("🔊 SoundManager: isMobile=$isMobile, useMP3=$useMP3 (isSafari=$isSafari, isIOS=$isIOS)")
        
        isMuted = !SettingsManager.isSoundEnabled()
        
        // Create AudioContext
        try {
            audioContext = js("new (window.AudioContext || window.webkitAudioContext)()")
            console.log("🔊 AudioContext created, state: ${audioContext.state}")
            
            // Monitor state changes
            audioContext.onstatechange = {
                console.log("🔊 AudioContext state changed to: ${audioContext.state}")
                if (audioContext.state == "running") {
                    audioUnlocked = true
                }
            }
        } catch (e: Exception) {
            console.error("🔊 Failed to create AudioContext: ${e.message}")
        }
        
        // Load all sounds as Web Audio buffers
        loadAllBuffers()
        
        // Set up unlock listeners
        setupUnlockListeners()
    }
    
    private fun loadAllBuffers() {
        // Use MP3 for Safari/iOS and OGG for others
        val extensions = if (useMP3) listOf("mp3") else listOf("ogg")
        console.log("🔊 Loading ${soundKeys.size} sound buffers sequentially (extensions: $extensions)...")
        
        // Start sequential loading chain
        loadNextBuffer(0, extensions)
    }
    
    private fun loadNextBuffer(index: Int, extensions: List<String>) {
        if (index >= soundKeys.size) {
            console.log("✅ All sound buffers loaded sequence complete")
            return
        }
        
        val key = soundKeys[index]
        // Load current buffer, then schedule next one
        loadBufferWithFallbacks(key, extensions, 0) {
            // Add a small 50ms delay between requests to avoid 429 rate limiting
            window.setTimeout({ 
                loadNextBuffer(index + 1, extensions) 
            }, 50)
        }
    }
    
    private fun loadBufferWithFallbacks(key: String, extensions: List<String>, index: Int = 0, onComplete: () -> Unit = {}) {
        if (index >= extensions.size) {
            console.error("❌ Failed to load $key with all provided extensions")
            onComplete()
            return
        }
        
        val ext = extensions[index]
        val filename = "$key.$ext"
        
        if (audioContext == null) {
            console.error("🔊 Cannot load $key - no AudioContext")
            onComplete()
            return
        }
        
        try {
            val xhr = XMLHttpRequest()
            xhr.open("GET", filename, true)
            xhr.asDynamic().responseType = "arraybuffer"
            
            xhr.onload = {
                if (xhr.status.toInt() == 200) {
                    val response = xhr.response
                    audioContext.decodeAudioData(response,
                        { buffer: dynamic ->
                            audioBuffers[key] = buffer
                            console.log("✅ Buffer decoded: $key ($ext)")
                            onComplete()
                        },
                        { err: dynamic ->
                            console.error("❌ Decode failed $key ($ext): $err")
                            // Try next fallback
                            loadBufferWithFallbacks(key, extensions, index + 1, onComplete)
                        }
                    )
                } else if (xhr.status.toInt() == 404) {
                    // Try next fallback on 404 WITHOUT logging error
                    loadBufferWithFallbacks(key, extensions, index + 1, onComplete)
                } else if (xhr.status.toInt() == 429) {
                     // Rate limited - wait longer and retry same extension
                     console.warn("⚠️ Rate limited (429) on $key - retrying in 1s...")
                     window.setTimeout({
                         loadBufferWithFallbacks(key, extensions, index, onComplete)
                     }, 1000)
                } else {
                    console.error("❌ XHR failed for $key ($ext): status=${xhr.status}")
                    loadBufferWithFallbacks(key, extensions, index + 1, onComplete)
                }
            }
            xhr.onerror = { 
                loadBufferWithFallbacks(key, extensions, index + 1, onComplete)
            }
            xhr.send()
        } catch (e: Exception) {
            console.error("❌ Load exception $key ($ext): ${e.message}")
            onComplete()
        }
    }

    private fun setupUnlockListeners() {
        val handler: (Event) -> Unit = { unlockAudio() }
        document.addEventListener("touchstart", handler)
        document.addEventListener("touchend", handler)
        document.addEventListener("click", handler)
        
        // Handle visibility changes to resume audio when returning to the tab
        document.addEventListener("visibilitychange", {
            if (document.asDynamic().visibilityState == "visible") {
                console.log("🔊 Tab visible again, attempting audio resume...")
                unlockAudio()
            }
        })
    }
    
    private fun primeAudio() {
        if (audioContext == null) return
        try {
            // Defensive: Ensure sampleRate is a number, default to 44100
            val srVal = audioContext.sampleRate
            val sr = (if (srVal != null) srVal else 44100.0) as Double
            
            // Use 0.05s of silence - shorter to minimize impact
            val length = (sr * 0.05).toInt().coerceAtLeast(1)
            val buffer = audioContext.createBuffer(1, length, sr)
            val source = audioContext.createBufferSource()
            source.buffer = buffer
            
            // Apply gain ramp to prevent any potential popping on start/stop
            val gain = audioContext.createGain()
            gain.gain.value = 0.0 // Force absolute valid zero
            
            source.connect(gain)
            gain.connect(audioContext.destination)
            source.start(0)
            console.log("🔊 Audio primed (soft silent 0.05s)")
        } catch (e: Exception) {
            console.error("❌ Audio prime failed: ${e.message}")
        }
    }

    /**
     * Start a silent looping buffer to keep audio hardware active on mobile.
     */
    private fun startKeepAlive() {
        if (audioContext == null || !isMobile) return
        if (keepAliveSource != null) return // Already running
        
        console.log("🔊 Starting silent keep-alive loop...")
        try {
            if (keepAliveBuffer == null) {
                // Create 2 seconds of silence
                keepAliveBuffer = audioContext.createBuffer(1, audioContext.sampleRate * 2, audioContext.sampleRate)
            }
            
            val source = audioContext.createBufferSource()
            source.buffer = keepAliveBuffer
            source.loop = true
            
            // Self-healing: restart if it stops for any reason
            source.onended = {
                console.log("🔊 Keep-alive loop ended - restarting...")
                keepAliveSource = null
                startKeepAlive()
            }
            
            val gain = audioContext.createGain()
            gain.gain.value = 0.001 // Nearly silent but active
            
            source.connect(gain)
            gain.connect(audioContext.destination)
            source.start(0)
            
            keepAliveSource = source
            
            // Also start periodic heartbeat for extra redundancy
            startHeartbeat()
        } catch (e: Exception) {
            console.error("❌ Failed to start keep-alive: ${e.message}")
        }
    }
    
    /**
     * Periodic silent heartbeat to prevent hardware suspension.
     */
    private fun startHeartbeat() {
        if (heartbeatIntervalId != null) return
        console.log("🔊 Starting 10s audio heartbeat")
        heartbeatIntervalId = window.setInterval({
            if (audioUnlocked) {
                console.log("🔊 Heartbeat pulse...")
                joltAudio()
            }
        }, 10000)
    }
    
    /**
     * Start a hidden looping <audio> element with a silent track.
     * This is a "Nuclear Fix" for mobile browsers that suspend Web Audio.
     */
    private fun startHybridKeepAlive() {
        if (!isMobile) return
        if (hybridAudio != null) return
        
        console.log("🔊 Starting Hybrid HTML Audio keep-alive (silent track)...")
        try {
            val audio = js("new Audio()")
            // 1-second silent WAV to keep the audio channel open without clicking
            audio.src = "data:audio/wav;base64,UklGRmYAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YUIAAACAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA="
            audio.loop = true
            audio.volume = 0.001 // Nearly silent
            audio.muted = false  // Must NOT be muted or iOS might ignore it
            
            // This MUST be called from a user gesture, so we call it in unlockAudio
            hybridAudio = audio
            console.log("🔊 Hybrid HTML Audio initialized (silent)")
        } catch (e: Exception) {
            console.error("❌ Failed to start hybrid keep-alive: ${e.message}")
        }
    }

    /**
     * Forcibly jolt all audio systems synchronously.
     * This is the strongest way to wake up audio on mobile.
     */
    fun joltAudio() {
        if (audioContext == null) return
        
        // Path 1: Web Audio Context prime
        primeAudio()
        
        // Path 2: Web Audio Context resume
        if (audioContext.state == "suspended" || audioContext.state == "interrupted") {
            try { audioContext.resume() } catch (e: Exception) {}
        }
        
        // Path 3: HTML Audio jolt (if initialized)
        try {
            val audio = hybridAudio
            if (audio != null) {
                // REMOVED .asDynamic() because hybridAudio is already dynamic!
                if (audio.paused == true) {
                    audio.play()
                }
            }
        } catch (e: Exception) {
            // Ignore silent play failures
        }
    }
    
    fun unlockAudio() {
        if (audioContext == null) return
        
        val state = audioContext.state as? String
        console.log("🔊 unlockAudio (Phase 5) called, current state: $state")
        
        // Multi-Path Synchronous Jolt
        joltAudio()
        
        // Start Hybrid Keep-Alive (HTML Audio)
        startHybridKeepAlive()
        try {
            hybridAudio?.play() // Initial play from gesture
        } catch (e: Exception) {
            console.error("❌ Hybrid audio play failed: ${e.message}")
        }
        
        if (state == "suspended" || state == "interrupted") {
            try {
                audioContext.resume().then {
                    console.log("🔊 AudioContext resumed successfully!")
                    audioUnlocked = true
                    startKeepAlive() // Keep Web Audio loop alive
                    null
                }.catch { err: dynamic ->
                    console.error("❌ AudioContext resume failed: $err")
                }
            } catch (e: Exception) {
                console.error("❌ AudioContext resume exception: ${e.message}")
            }
        } else if (state == "running") {
            audioUnlocked = true
            startKeepAlive()
        }
    }
    
    /**
     * Called directly from user tap on overlay - plays audible sound.
     */
    fun playTestSound() {
        console.log("🔊 playTestSound - direct from user tap")
        
        // Resume context first
        if (audioContext != null) {
            val state = audioContext.state as? String
            if (state == "suspended") {
                audioContext.resume()
            }
        }
        
        // Play actual sound immediately
        playBuffer("pin_placed", 0.3)
        audioUnlocked = true
    }
    
    private fun playBuffer(key: String, volume: Double = 1.0) {
        if (audioContext == null) {
            console.warn("🔊 No AudioContext")
            return
        }
        
        val buffer = audioBuffers[key]
        if (buffer == null) {
            console.warn("🔊 Buffer not loaded: $key")
            return
        }
        
        try {
            // Ensure context is running
            val state = audioContext.state as? String
            if (state == "suspended") {
                audioContext.resume()
            }
            
            val source = audioContext.createBufferSource()
            source.buffer = buffer
            
            val gain = audioContext.createGain()
            gain.gain.value = masterVolume * volume
            
            source.connect(gain)
            gain.connect(audioContext.destination)
            source.start(0)
            
            console.log("🎵 Playing buffer: $key")
        } catch (e: Exception) {
            console.error("❌ Play error $key: ${e.message}")
        }
    }

    private fun playSound(key: String, volume: Double = 1.0) {
        if (isMuted || !SettingsManager.isSoundEnabled()) {
            console.log("🔇 Sound $key skipped - muted")
            return
        }
        
        console.log("🔊 playSound called: $key (buffers loaded: ${audioBuffers.size})")
        
        // Resume context if needed
        val ctx = audioContext
        if (ctx != null) {
            val state = ctx.state as? String
            if (state == "suspended") {
                console.log("🔊 Resuming suspended AudioContext before playing $key")
                ctx.resume().then {
                    playBuffer(key, volume)
                    null
                }.catch { err: dynamic ->
                    console.error("❌ Failed to resume AudioContext: $err")
                    // Try to play anyway as fallback
                    playBuffer(key, volume)
                }
                return
            }
        }
        
        playBuffer(key, volume)
    }
    
    fun playScoreSound(points: Int) {
        if (!initialized) initialize()
        if (isMuted || !SettingsManager.isSoundEnabled()) {
            console.log("🔇 Score sound skipped - muted or disabled")
            return
        }
        
        console.log("🔊 playScoreSound: $points points")
        
        when {
            points >= 10 -> playSound("score_epic")
            points >= 6 -> playSound("score_great")
            points >= 4 -> playSound("score_great")
            points == 3 -> playSound("score_basic_3")
            points == 2 -> playSound("score_basic_2")
            points == 1 -> playSound("score_basic")
            else -> console.log("🔊 No sound for $points points")
        }
    }
    
    fun playPinPlaced() {
        if (!initialized) initialize()
        if (!SettingsManager.isSoundEnabled()) return
        playSound("pin_placed")
    }
    
    fun playPinSelected() {
        if (!initialized) initialize()
        if (!SettingsManager.isSoundEnabled()) return
        playSound("pin_selected")
    }
    
    fun playPinsReversed() {
        if (!initialized) initialize()
        if (!SettingsManager.isSoundEnabled()) return
        playSound("pins_reversed")
    }
    
    fun playJokerCreated() {
        if (!initialized) initialize()
        if (!SettingsManager.isSoundEnabled()) return
        playSound("joker_created")
    }
    
    fun playError() {
        if (!initialized) initialize()
        if (!SettingsManager.isSoundEnabled()) return
        playSound("error")
    }
    
    fun setMuted(muted: Boolean) {
        isMuted = muted
        SettingsManager.setSetting("soundEnabled", !muted)
    }
    
    fun toggleMute(): Boolean {
        isMuted = !isMuted
        SettingsManager.setSetting("soundEnabled", !isMuted)
        return isMuted
    }
    
    fun setVolume(volume: Float) {
        masterVolume = volume.coerceIn(0f, 1f)
    }
    
    fun getMuted(): Boolean = isMuted || !SettingsManager.isSoundEnabled()
    fun getVolume(): Float = masterVolume
}
